/** Aggregate telemetry health. No event payloads or actor/resource dimensions. */
export const ANALYTICS_DIAGNOSTIC_COUNTERS = [
  'queued',
  'sdk_accepted',
  'dropped_queue_capacity',
  'schema_rejected',
  'missing_context',
  'missing_session',
  'legacy_fallback',
  'sdk_not_sent',
  'sdk_no_destination',
  'sdk_invalid_request',
  'sdk_payload_too_large',
  'sdk_rate_limited',
  'sdk_failed',
  'sdk_threw',
  'sdk_promise_rejected',
  'sdk_initialization_failed',
  'storage_unavailable',
  'offline_persistence_truncated',
  'duplicate_suppressed',
  'stale_context',
  'invalid_context',
  'handoff_unavailable',
] as const;

export type AnalyticsDiagnosticCounter = (typeof ANALYTICS_DIAGNOSTIC_COUNTERS)[number];
export type AnalyticsDiagnosticCounters = Readonly<Record<AnalyticsDiagnosticCounter, number>>;

export interface AnalyticsDiagnosticsContext {
  environment: string;
  release_id: string;
  schema_version: number;
  enabled: boolean;
  server_zone: 'US' | 'EU';
  /** Build-provided fingerprint only; never the Amplitude API key. */
  project_fingerprint?: string;
}

export interface AnalyticsDiagnosticsSnapshot {
  /** Cumulative counters for snapshot(); interval deltas for sink reports. */
  counters: AnalyticsDiagnosticCounters;
  pending: number;
  pending_peak: number;
}

export interface AnalyticsHealthReport extends AnalyticsDiagnosticsSnapshot {
  context: Readonly<AnalyticsDiagnosticsContext>;
  interval_started_at_ms: number;
  reported_at_ms: number;
}

export type AnalyticsDiagnosticsSink = (report: AnalyticsHealthReport) => void | Promise<void>;

const COUNTER_NAMES = new Set<string>(ANALYTICS_DIAGNOSTIC_COUNTERS);
const REPORT_INTERVAL_MS = 60_000;
const ENVIRONMENTS = new Set([
  'production', 'staging', 'development', 'test', 'preview', 'unknown',
]);
const emptyCounters = () => Object.fromEntries(
  ANALYTICS_DIAGNOSTIC_COUNTERS.map((name) => [name, 0]),
) as Record<AnalyticsDiagnosticCounter, number>;

function boundedContext(input: AnalyticsDiagnosticsContext): Readonly<AnalyticsDiagnosticsContext> {
  return Object.freeze({
    environment: ENVIRONMENTS.has(input.environment) ? input.environment : 'unknown',
    release_id: typeof input.release_id === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,127}$/.test(input.release_id)
      ? input.release_id : 'unknown',
    schema_version: Number.isSafeInteger(input.schema_version) && input.schema_version > 0
      ? input.schema_version : 0,
    enabled: input.enabled === true,
    server_zone: input.server_zone === 'EU' ? 'EU' : 'US',
    ...(input.project_fingerprint === undefined ? {} : {
      project_fingerprint: typeof input.project_fingerprint === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(input.project_fingerprint)
        ? input.project_fingerprint : 'invalid',
    }),
  });
}

/**
 * A fixed-size accumulator independent of the Amplitude transport.
 * A successful sink call means handed to the monitoring SDK, not ingested.
 */
export class AnalyticsDiagnostics {
  private counters = emptyCounters();
  private reportedCounters = emptyCounters();
  private pending = 0;
  private pendingPeak = 0;
  private revision = 0;
  private reportedRevision = 0;
  private intervalStartedAt: number;
  private context?: Readonly<AnalyticsDiagnosticsContext>;
  private sink?: AnalyticsDiagnosticsSink;
  private timer?: ReturnType<typeof setInterval>;
  private flushing = false;
  private lifecycle = 0;

  constructor(private readonly now: () => number = () => Date.now()) {
    this.intervalStartedAt = now();
  }

  increment(name: AnalyticsDiagnosticCounter, amount = 1): void {
    // Runtime validation prevents accidental IDs or messages from becoming keys.
    if (!COUNTER_NAMES.has(name) || !Number.isFinite(amount) || !Number.isInteger(amount) || amount <= 0) return;
    const next = Math.min(Number.MAX_SAFE_INTEGER, this.counters[name] + amount);
    if (next === this.counters[name]) return;
    this.counters[name] = next;
    this.revision++;
  }

  setPending(value: number): void {
    if (!Number.isFinite(value)) return;
    const next = Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.trunc(value)));
    if (next === this.pending) return;
    this.pending = next;
    this.pendingPeak = Math.max(this.pendingPeak, next);
    this.revision++;
  }

  /** SDK promises can resolve unsuccessful results rather than reject. */
  classifyResult(code: number): void {
    const counter: AnalyticsDiagnosticCounter = code === 200 ? 'sdk_accepted'
      : code === 0 ? 'sdk_not_sent'
      : code === 100 ? 'sdk_no_destination'
      : code === 400 ? 'sdk_invalid_request'
      : code === 413 ? 'sdk_payload_too_large'
      : code === 429 ? 'sdk_rate_limited'
      : 'sdk_failed';
    this.increment(counter);
  }

  snapshot(): AnalyticsDiagnosticsSnapshot {
    return Object.freeze({
      counters: Object.freeze({ ...this.counters }),
      pending: this.pending,
      pending_peak: this.pendingPeak,
    });
  }

  /** Registers one sink and timer, even if application setup is replayed. */
  start(context: AnalyticsDiagnosticsContext, sink: AnalyticsDiagnosticsSink): void {
    if (this.sink) return;
    this.context = boundedContext(context);
    this.sink = sink;
    this.lifecycle++;
    this.revision++; // One initial configuration report, including disabled builds.
    this.timer = setInterval(() => this.flush(), REPORT_INTERVAL_MS);
    if (typeof window !== 'undefined') window.addEventListener('pagehide', this.onPageHide);
    this.flush();
  }

  private onPageHide = () => this.flush();

  flush(): void {
    if (!this.sink || !this.context || this.flushing || this.revision === this.reportedRevision) return;
    const snapshot = this.snapshot();
    const revision = this.revision;
    const lifecycle = this.lifecycle;
    const reportedAt = this.now();
    const counters = emptyCounters();
    for (const name of ANALYTICS_DIAGNOSTIC_COUNTERS) {
      counters[name] = snapshot.counters[name] - this.reportedCounters[name];
    }
    const report: AnalyticsHealthReport = Object.freeze({
      ...snapshot,
      counters: Object.freeze(counters),
      context: this.context,
      interval_started_at_ms: this.intervalStartedAt,
      reported_at_ms: reportedAt,
    });
    const deliveredToSink = () => {
      if (lifecycle !== this.lifecycle) return;
      this.reportedCounters = { ...snapshot.counters };
      this.reportedRevision = revision;
      this.intervalStartedAt = reportedAt;
      this.flushing = false;
    };
    const failedSink = () => {
      if (lifecycle === this.lifecycle) this.flushing = false;
      // Keep deltas for the next attempt; never recursively report sink failures.
    };
    this.flushing = true;
    try {
      const pending = this.sink(report);
      if (pending && typeof pending.then === 'function') {
        void Promise.resolve(pending).then(deliveredToSink, failedSink);
      } else {
        deliveredToSink();
      }
    } catch {
      failedSink();
    }
  }

  dispose(): void {
    this.flush();
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', this.onPageHide);
    this.lifecycle++;
    this.flushing = false;
    this.sink = undefined;
    this.context = undefined;
  }
}

export const analyticsDiagnostics = new AnalyticsDiagnostics();
